import { NgModule } from '@angular/core';
import {
  environment,
  HTTP_AUTH_INTERCEPTOR,
} from '@ups/xplat/core';
import { AuthModule as SharedAuthModule } from '@ups/user';
import {
  AuthHttpInterceptor,
  AuthModule as Auth0Module,
} from '@auth0/auth0-angular';
import {
  Auth0AuthenticationService,
  AuthenticationService,
} from '@ups/security';
import { LoginService } from './login.service';
import { Auth0LoginService } from './auth0-login.service';
import { resolveAuth0RenewalOptions } from './auth0-renewal-options';
import {
  AUTH0_AUTHORIZE_TIMEOUT_SECONDS,
  AUTH0_HTTP_TIMEOUT_SECONDS,
} from './token-acquire-bound';

// Explicit, shorter bounds on auth0-spa-js's own silent-renewal steps
// (#32087); see token-acquire-bound.ts for the values and the ordering rule
// against the interceptor bound. `httpTimeoutInSeconds` is a spa-js option
// that auth0-angular 1.x forwards verbatim (Auth0ClientFactory spreads the
// config into `new Auth0Client(...)`) but omits from its `AuthConfig` typing,
// hence the spread through a loosely typed object.
const auth0SdkTimeouts: Record<string, number> = {
  authorizeTimeoutInSeconds:
    AUTH0_AUTHORIZE_TIMEOUT_SECONDS,
  httpTimeoutInSeconds: AUTH0_HTTP_TIMEOUT_SECONDS,
};

// Auth0 never skips the consent screen for a localhost origin, so on local
// serves we renew via refresh tokens instead of the consent-blocked iframe.
// Deployed origins are env-driven (useRefreshTokens + cacheLocation). See
// auth0-renewal-options.ts for the full rationale.
const auth0Renewal = resolveAuth0RenewalOptions(
  typeof window !== 'undefined'
    ? window.location.hostname
    : undefined,
  environment.auth0.useRefreshTokens,
  environment.auth0.cacheLocation
);

@NgModule({
  imports: [
    SharedAuthModule,
    Auth0Module.forRoot({
      clientId: environment.auth0.clientId,
      domain: environment.auth0.domain,
      audience: environment.auth0.audience,
      useRefreshTokens: auth0Renewal.useRefreshTokens,
      cacheLocation: auth0Renewal.cacheLocation,
      ...auth0SdkTimeouts,
      httpInterceptor: {
        allowedList: [
          {
            uri: '*',
            tokenOptions: {
              audience: environment.auth0.audience,
            },
          },
        ],
      },
    }),
  ],
  providers: [
    // AuthHttpInterceptor is provided but NOT registered globally
    // It's manually added by MyHttpClientFactory only when auth=true
    {
      provide: HTTP_AUTH_INTERCEPTOR,
      useClass: AuthHttpInterceptor,
    },
    {
      provide: AuthenticationService,
      useClass: Auth0AuthenticationService,
    },
    {
      provide: LoginService,
      useClass: Auth0LoginService,
    },
    // Removed global HTTP_INTERCEPTORS registration to allow public endpoints
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: AuthHttpInterceptor,
    //   multi: true,
    // },
  ],
})
export class AuthModule {}
